import { toast } from 'sonner';
import { createMutationHook } from '@/hooks/lib/createMutationHook';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { leadEndpoints } from '@/lib/api/endpoints';
import { leadKeys } from '@/lib/queryKeys';
import type { CreateLeadSavedViewDto, LeadSavedView, UpdateLeadSavedViewDto } from '@/types/lead';

export function useLeadSavedViews() {
  return useApiQuery<LeadSavedView[]>({
    queryKey: leadKeys.savedViews(),
    queryFn: () => apiGet<LeadSavedView[]>(operationsClient, leadEndpoints.savedViews()),
    showErrorToast: false,
  });
}

export const useCreateLeadSavedView = createMutationHook<LeadSavedView, CreateLeadSavedViewDto>({
  mutationFn: (dto) =>
    apiPost<LeadSavedView, CreateLeadSavedViewDto>(
      operationsClient,
      leadEndpoints.savedViews(),
      dto,
    ),
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: leadKeys.savedViews() });
    toast.success('Lead view saved');
  },
});

export const useUpdateLeadSavedView = createMutationHook<LeadSavedView, UpdateLeadSavedViewDto>({
  mutationFn: ({ id, ...dto }) =>
    apiPatch<LeadSavedView, Omit<UpdateLeadSavedViewDto, 'id'>>(
      operationsClient,
      leadEndpoints.savedView(id),
      dto,
    ),
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: leadKeys.savedViews() });
    toast.success('Lead view updated');
  },
});

export const useDeleteLeadSavedView = createMutationHook<void, string>({
  mutationFn: (id) => apiDelete<void>(operationsClient, leadEndpoints.savedView(id)),
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: leadKeys.savedViews() });
    toast.success('Lead view deleted');
  },
});
